import { defineStore } from "pinia";
import type {
  WebModelGetCompany,
  WebModelPostCompany,
  WebModelPutUpdateCompany,
  WebModelPutUpdateCompanyProfile,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  findCompany,
  removeFromCompany,
  updateCompany,
  deleteCompany,
  deleteCompanyEBillingAddress,
  resendCompanyEBillingVerificationLink,
  postCompanyEBillingAddress,
  resendCompanyCreditNoteEmailVerificationLink,
  deleteCompanyCreditNoteEmailAddress,
  postCompanyCreditNoteEmailAddress,
  createCompanyWithUserAndAddress,
  addCompanyProfile,
  verifyEBillingAddress,
  verifyCreditNoteEmailAddress,
} from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import { updateCompanyProfile } from "~/model/multipartRequests";

interface State {
  userCompany: WebModelGetCompany | null;
  firstLoaded: boolean;
}

export const useCompanyStore = defineStore("company", {
  // convert to a function
  state: (): State => ({
    userCompany: null, // initial state
    firstLoaded: false,
  }),
  getters: {
    // this getter is sometimes too late for middleware
    userHasCompany: (state) => {
      return state.userCompany != null;
    },
  },
  actions: {
    async createCompany(webModelPostCompany: WebModelPostCompany) {
      const { data } = await withErrorMessage(
        withLoading(createCompanyWithUserAndAddress(webModelPostCompany)),
      );
      console.debug("createCompany: ", data);
      if (data) {
        this.userCompany = data;
      }
      await useUserStore().loadAuthUser();
    },

    async addCompanyProfile(companyId: string, email: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(addCompanyProfile({ companyId, email }))),
      );
      console.debug("addCompanyProfile: ", data, error);
      if (data && !error) {
        await this.loadUserCompany(companyId);
        return true;
      }
      return false;
    },

    async editCompanyProfile(
      json: WebModelPutUpdateCompanyProfile,
      iconFile: Blob | undefined,
      bannerFile: Blob | undefined,
      privacyPolicyFile: Blob | undefined,
    ) {
      const { data } = await withErrorMessage(
        withLoading(
          updateCompanyProfile({
            json,
            iconFile,
            bannerFile,
            privacyPolicyFile,
          }),
        ),
      );
      console.debug("editCompanyProfile: ", data);
      if (data) {
        await this.loadUserCompany(data.companyId);
        return true;
      }
      return false;
    },

    async loadUserCompany(companyId: string) {
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(findCompany(companyId))),
      );
      console.debug("loadUserCompany: ", data, error);
      if (data) {
        this.userCompany = data;
        this.firstLoaded = true;
      }
    },

    /**
     * because on full page refresh the userCompany is not loaded before the middleware
     * is executed and the user is redirected even if he has a company
     */
    async ensureLoaded() {
      if (this.userCompany == null && !this.firstLoaded) {
        const userStore = useUserStore();
        const companyId = userStore.authUser?.companyId;
        if (companyId) {
          await this.loadUserCompany(companyId);
        }
      }
    },

    async updateCompany(webModelPutUpdateCompany: WebModelPutUpdateCompany) {
      const { data } = await withErrorMessage(
        withLoading(updateCompany(webModelPutUpdateCompany)),
      );
      console.debug("updateCompany: ", data);
      if (data) {
        this.userCompany = data;
        await useUserStore().loadUserRegistrationStatus();
      }
    },

    async removeUserFromCompany(userUuid: string, companyId: string) {
      const userStore = useUserStore();
      try {
        await withErrorMessage(
          withLoading(
            removeFromCompany({ uuid: userUuid, "company-id": companyId }),
          ),
        );
        await withLoading(
          Promise.all([
            withErrorMessage(userStore.loadUserRegistrationStatus()),
            withErrorMessage(userStore.loadAuthUser()),
            withErrorMessage(userStore.loadAuthUserRoles()),
            withErrorMessage(userStore.loadUserHub()),
          ]),
        );
        console.info("removed User " + userUuid + " from company " + companyId);
      } catch (e) {
        console.error(e);
      }
    },
    async deleteCompany(companyId: string) {
      console.debug("deleteCompany called");
      const userStore = useUserStore();
      try {
        await withErrorMessage(withLoading(deleteCompany(companyId)));
        await withLoading(
          Promise.all([
            withErrorMessage(userStore.loadUserRegistrationStatus()),
            withErrorMessage(userStore.loadAuthUser()),
            withErrorMessage(userStore.loadAuthUserRoles()),
            withErrorMessage(userStore.loadUserHub()),
          ]),
        );
        console.info("deleteCompany " + companyId);
        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },

    //region ebilling
    async resendEBillingAddressLink(companyId: string) {
      const { error } = await withErrorHandling(
        withErrorMessage(
          withLoading(resendCompanyEBillingVerificationLink(companyId)),
        ),
      );
      console.debug("resendEBillingAddressLink: ", error);
      if (!error) {
        return true;
      }
      return false;
    },
    async deleteEBillingAddress(companyId: string) {
      const { error } = await withErrorHandling(
        withErrorMessage(withLoading(deleteCompanyEBillingAddress(companyId))),
      );
      console.debug("deleteEBillingAddress: ", error);
      if (!error) {
        await this.loadUserCompany(companyId);
        return true;
      }
      return false;
    },
    async postCompanyEBillingAddress(companyId: string, ebillingEmail: string) {
      await withErrorMessage(
        withLoading(postCompanyEBillingAddress(companyId, { ebillingEmail })),
      );
      console.debug("postCompanyEBillingAddress");
      await this.loadUserCompany(companyId);
      return true;
    },
    async verifyEBillingAddress(companyId: string, code: string) {
      await withErrorMessage(
        withLoading(verifyEBillingAddress(companyId, { code })),
      );
      console.debug("verifyEBillingAddress");
    },
    //endregion

    //region creditnote
    async resendCreditNoteAddressLink(companyProfileId: number) {
      const { error } = await withErrorHandling(
        withErrorMessage(
          withLoading(
            resendCompanyCreditNoteEmailVerificationLink(companyProfileId),
          ),
        ),
      );
      console.debug("resendCreditNoteAddressLink: ", error);
      if (!error) {
        return true;
      }
      return false;
    },
    async deleteCompanyCreditNoteAddress(
      companyId: string,
      companyProfileId: number,
    ) {
      const { error } = await withErrorHandling(
        withErrorMessage(
          withLoading(deleteCompanyCreditNoteEmailAddress(companyProfileId)),
        ),
      );
      console.debug("deleteCompanyCreditNoteAddress: ", error);
      if (!error) {
        await this.loadUserCompany(companyId);
        return true;
      }
      return false;
    },
    async postCompanyCreditNoteAddress(
      companyId: string,
      companyProfileId: number,
      email: string,
    ) {
      console.debug("postCompanyCreditNoteAddress: ");
      await withErrorMessage(
        withLoading(
          postCompanyCreditNoteEmailAddress(companyProfileId, {
            email,
          }),
        ),
      );
      await this.loadUserCompany(companyId);
      return true;
    },

    async verifyCreditNoteEmailAddress(companyProfileId: number, code: string) {
      await withErrorMessage(
        withLoading(verifyCreditNoteEmailAddress(companyProfileId, { code })),
      );
      console.debug("verifyCreditNoteEmailAddress");
    },

    //endregion

    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
  },
});
